import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { useParams } from 'react-router-dom'
import { Alert, Button, Flex, Form, Input, Select, Space, Typography, message } from 'antd'
import * as Layout from 'views/layouts/MainLayout/MainLayout'
import * as Header from 'views/ui/Header/Header'
import { useHelmet, useHistoryPush } from '@agro-club/frontend-shared'
import { useTranslation } from 'react-i18next'
import { useSelector } from 'react-redux'
import AuthSelectors from 'modules/domain/auth/selectors'
import { SeedPedigree, SeedTracker } from 'modules/domain/seedTracker/types'
import { createSeedInventory, getSeedTrackersList } from 'modules/domain/seedTracker/managers'
import { getSeedInventoryGrowerCompanies } from 'modules/domain/company/managers'
import { addNote } from 'modules/domain/note/managers'
import { NoteType } from 'types/entities'
import Routes from '../routes'
import { PedigreeTag } from './PedigreeTag'
import {
  inputUnitSuffixTextStyle,
  seedInventoryAddContentStyle,
  seedInventoryAddFieldCellStyle,
  seedInventoryAddFormPagePaddingStyle,
  seedInventoryAddFormStyle,
  seedInventoryAddSectionBlockStyle,
  seedInventoryAddSectionTitleStyle,
  seedInventoryAddSeededPedigreePlaceholderStyle,
  seedInventoryAddSeedingIntentionsTopRowStyle,
  seedInventoryAddWarningAlertStyle,
} from '../styles'

export type SeedInventoryAddFormValues = {
  company_id?: string
  location?: string
  crop_certificate_id?: string
  disposed_mt?: string
  on_hand_mt?: string
  clean_yield_actual_mt?: string
  clean_yield_estimate_mt?: string
  gross_yield_actual_bu?: string
  gross_yield_estimate_bu?: string
  initial_germ_actual?: string
  initial_germ_estimate?: string
  final_germ_actual?: string
  final_germ_estimate?: string
  diseased_actual?: string
  diseased_estimate?: string
  planned_seeded_area?: string
  current_seeded_area_actual?: string
  current_seeded_area_estimate?: string
  seeded_pedigree?: SeedPedigree
  projected_pedigree?: SeedPedigree
  projected_yield_actual_bu?: string
  projected_yield_estimate_bu?: string
  note_text?: string
}

const pedigreeOptions = Object.values(SeedPedigree).map((v) => ({ label: v, value: v }))

const pedigreeSet = new Set(Object.values(SeedPedigree))

const OPTIONAL_SEED_INVENTORY_BODY_KEYS: (keyof SeedInventoryAddFormValues)[] = [
  'crop_certificate_id',
  'disposed_mt',
  'on_hand_mt',
  'clean_yield_actual_mt',
  'clean_yield_estimate_mt',
  'gross_yield_actual_bu',
  'gross_yield_estimate_bu',
  'initial_germ_actual',
  'initial_germ_estimate',
  'final_germ_actual',
  'final_germ_estimate',
  'diseased_actual',
  'diseased_estimate',
  'planned_seeded_area',
  'current_seeded_area_actual',
  'current_seeded_area_estimate',
  'projected_yield_actual_bu',
  'projected_yield_estimate_bu',
]

function buildCreateSeedInventoryBody(
  productCardId: string,
  values: SeedInventoryAddFormValues,
): Record<string, unknown> {
  const body: Record<string, unknown> = {
    product_card_id: productCardId,
    company_id: values.company_id,
    year: String(new Date().getFullYear()),
    seeded_pedigree: values.seeded_pedigree,
    projected_pedigree: values.projected_pedigree,
  }
  for (const key of OPTIONAL_SEED_INVENTORY_BODY_KEYS) {
    const raw = values[key]
    if (typeof raw === 'string' && raw.trim() !== '') {
      body[key] = raw.trim()
    }
  }
  return body
}

const InputUnitSuffix: React.FC<{ unit: string }> = ({ unit }) => (
  <Typography.Text type="secondary" style={inputUnitSuffixTextStyle}>
    {unit}
  </Typography.Text>
)

const SeedInventoryAdd: React.FC = () => {
  const { id: productCardId } = useParams<{ id: string }>()
  const push = useHistoryPush()
  const { t } = useTranslation(['seedTracker', 'common', 'validation', 'errors'])
  const profile = useSelector(AuthSelectors.profile)
  const [form] = Form.useForm<SeedInventoryAddFormValues>()
  const seededPedigreeWatched = Form.useWatch('seeded_pedigree', form)
  const [growerOptions, setGrowerOptions] = useState<{ label: string; value: string }[]>([])
  const [growersLoading, setGrowersLoading] = useState(false)
  const [submitting, setSubmitting] = useState(false)
  const [seedTracker, setSeedTracker] = useState<SeedTracker | undefined>(undefined)

  const goBack = useCallback(() => {
    if (productCardId) {
      push({ route: Routes.SeedInventoryList, params: { id: productCardId } })
    } else {
      push({ route: Routes.List })
    }
  }, [push, productCardId])

  useHelmet({ title: t('addInventory.metaTitle') })

  useEffect(() => {
    if (!productCardId) {
      setSeedTracker(undefined)
      return
    }

    let cancelled = false
    getSeedTrackersList({}, {}, 1, 5000)
      .then((res) => {
        if (!cancelled) {
          setSeedTracker((res.data ?? []).find((tracker) => tracker.id === productCardId))
        }
      })
      .catch(() => {
        if (!cancelled) {
          setSeedTracker(undefined)
        }
      })

    return () => {
      cancelled = true
    }
  }, [productCardId])

  useEffect(() => {
    let cancelled = false
    setGrowersLoading(true)
    getSeedInventoryGrowerCompanies()
      .then((res) => {
        if (cancelled) {
          return
        }
        setGrowerOptions(
          (res.data ?? []).map((c) => ({
            value: c.id,
            label: c.official_name,
          })),
        )
      })
      .catch(() => {
        if (!cancelled) {
          setGrowerOptions([])
        }
      })
      .finally(() => {
        if (!cancelled) {
          setGrowersLoading(false)
        }
      })
    return () => {
      cancelled = true
    }
  }, [])

  const handleFinish = useCallback(
    async (values: SeedInventoryAddFormValues) => {
      if (!productCardId) {
        return
      }
      setSubmitting(true)
      try {
        const createdInventory = await createSeedInventory(buildCreateSeedInventoryBody(productCardId, values))
        const noteText = values.note_text?.trim()
        const createdBy = `${profile?.first_name || ''} ${profile?.last_name || ''}`.trim()
        if (noteText && createdInventory?.id) {
          try {
            await addNote({
              parent_id: createdInventory.id,
              note_type: NoteType.SeedInventoryNote,
              note_text: noteText,
              created_by: createdBy || undefined,
            })
          } catch {
            void message.error('Seed inventory saved, but the note could not be saved.')
          }
        }
        message.success(t('addInventory.saveSuccess'))
        push({ route: Routes.SeedInventoryList, params: { id: productCardId } })
      } catch {
        message.error(t('unknown_error', { ns: 'errors' }))
      } finally {
        setSubmitting(false)
      }
    },
    [productCardId, profile?.first_name, profile?.last_name, push, t],
  )

  const fk = (key: string) => ['addInventory', 'fields', key].join('.')
  const sk = (key: string) => ['addInventory', 'sections', key].join('.')

  const selectPlaceholder = t('select', { ns: 'common' })
  const requiredMsg = t('field_required', { ns: 'validation' })

  const title = seedTracker
    ? `${t('addInventory.title')} - ${seedTracker.variety} - ${seedTracker.seed_class_name} ${
        seedTracker.seed_crop_name
      }`
    : t('addInventory.title')

  const seededPedigreeTag = useMemo(() => {
    if (seededPedigreeWatched != null && pedigreeSet.has(seededPedigreeWatched)) {
      return <PedigreeTag pedigree={seededPedigreeWatched} />
    }
    return (
      <Typography.Text type="secondary" style={seedInventoryAddSeededPedigreePlaceholderStyle}>
        —
      </Typography.Text>
    )
  }, [seededPedigreeWatched])

  return (
    <>
      <Layout.Header>
        <Header.Root onClickBack={goBack}>
          <Header.Title title={title} />
        </Header.Root>
      </Layout.Header>
      <Layout.Content style={seedInventoryAddContentStyle}>
        <div style={seedInventoryAddFormPagePaddingStyle}>
          {!productCardId ? (
            <Alert
              type="warning"
              message={t('addInventory.missingProductCard')}
              showIcon
              style={seedInventoryAddWarningAlertStyle}
              onClose={() => push({ route: Routes.List })}
            />
          ) : null}
          <Form form={form} layout="vertical" onFinish={handleFinish} style={seedInventoryAddFormStyle}>
            <div style={seedInventoryAddSectionBlockStyle}>
              <Typography.Text type="secondary" style={seedInventoryAddSectionTitleStyle}>
                {t(sk('inventoryDetails'))}
              </Typography.Text>
              <Flex gap={16} wrap align="flex-end">
                <Form.Item
                  name="seeded_pedigree"
                  label={t(fk('pedigree'))}
                  style={seedInventoryAddFieldCellStyle}
                  rules={[{ required: true, message: requiredMsg }]}
                >
                  <Select allowClear options={pedigreeOptions} placeholder={selectPlaceholder} size="large" />
                </Form.Item>
                <Form.Item
                  name="company_id"
                  label={t(fk('grower'))}
                  style={seedInventoryAddFieldCellStyle}
                  rules={[{ required: true, message: requiredMsg }]}
                >
                  <Select
                    allowClear
                    showSearch
                    optionFilterProp="label"
                    options={growerOptions}
                    placeholder={selectPlaceholder}
                    size="large"
                    loading={growersLoading}
                  />
                </Form.Item>
                {/*
                <Form.Item name="location" label={t(fk('location'))} style={seedInventoryAddFieldCellStyle}>
                  <Select disabled placeholder={t(fk('locationDisabledPlaceholder'))} size="large" options={[]} />
                </Form.Item>
                */}
                <Form.Item
                  name="crop_certificate_id"
                  label={t(fk('cropCertificateId'))}
                  style={seedInventoryAddFieldCellStyle}
                >
                  <Input size="large" />
                </Form.Item>
              </Flex>
            </div>

            <div style={seedInventoryAddSectionBlockStyle}>
              <Typography.Text type="secondary" style={seedInventoryAddSectionTitleStyle}>
                {t(sk('seedQuantities'))}
              </Typography.Text>
              <Flex gap={16} wrap align="flex-end">
                <Form.Item name="on_hand_mt" label={t(fk('onHand'))} style={seedInventoryAddFieldCellStyle}>
                  <Input
                    size="large"
                    suffix={<InputUnitSuffix unit={t('seedTracker:seedInventoryFields.units.mt')} />}
                  />
                </Form.Item>
                <Form.Item
                  name="clean_yield_actual_mt"
                  label={t(fk('cleanYieldAct'))}
                  style={seedInventoryAddFieldCellStyle}
                >
                  <Input
                    size="large"
                    suffix={<InputUnitSuffix unit={t('seedTracker:seedInventoryFields.units.mt')} />}
                  />
                </Form.Item>
                <Form.Item
                  name="clean_yield_estimate_mt"
                  label={t(fk('cleanYieldEst'))}
                  style={seedInventoryAddFieldCellStyle}
                >
                  <Input
                    size="large"
                    suffix={<InputUnitSuffix unit={t('seedTracker:seedInventoryFields.units.mt')} />}
                  />
                </Form.Item>
                <Form.Item
                  name="gross_yield_actual_bu"
                  label={t(fk('grossYieldAct'))}
                  style={seedInventoryAddFieldCellStyle}
                >
                  <Input
                    size="large"
                    suffix={<InputUnitSuffix unit={t('seedTracker:seedInventoryFields.units.bu')} />}
                  />
                </Form.Item>
                <Form.Item
                  name="gross_yield_estimate_bu"
                  label={t(fk('grossYieldEst'))}
                  style={seedInventoryAddFieldCellStyle}
                >
                  <Input
                    size="large"
                    suffix={<InputUnitSuffix unit={t('seedTracker:seedInventoryFields.units.bu')} />}
                  />
                </Form.Item>
                <Form.Item name="disposed_mt" label={t(fk('disposed'))} style={seedInventoryAddFieldCellStyle}>
                  <Input
                    size="large"
                    suffix={<InputUnitSuffix unit={t('seedTracker:seedInventoryFields.units.mt')} />}
                  />
                </Form.Item>
              </Flex>
            </div>

            <div style={seedInventoryAddSectionBlockStyle}>
              <Typography.Text type="secondary" style={seedInventoryAddSectionTitleStyle}>
                {t(sk('seedQuality'))}
              </Typography.Text>
              <Flex gap={16} wrap align="flex-end">
                <Form.Item
                  name="initial_germ_actual"
                  label={t(fk('initialGerminatedAct'))}
                  style={seedInventoryAddFieldCellStyle}
                >
                  <Input
                    size="large"
                    suffix={<InputUnitSuffix unit={t('seedTracker:seedInventoryFields.units.percentage')} />}
                  />
                </Form.Item>
                <Form.Item
                  name="initial_germ_estimate"
                  label={t(fk('initialGerminatedEst'))}
                  style={seedInventoryAddFieldCellStyle}
                >
                  <Input
                    size="large"
                    suffix={<InputUnitSuffix unit={t('seedTracker:seedInventoryFields.units.percentage')} />}
                  />
                </Form.Item>
                <Form.Item
                  name="final_germ_actual"
                  label={t(fk('finalGerminatedAct'))}
                  style={seedInventoryAddFieldCellStyle}
                >
                  <Input
                    size="large"
                    suffix={<InputUnitSuffix unit={t('seedTracker:seedInventoryFields.units.percentage')} />}
                  />
                </Form.Item>
                <Form.Item
                  name="final_germ_estimate"
                  label={t(fk('finalGerminatedEst'))}
                  style={seedInventoryAddFieldCellStyle}
                >
                  <Input
                    size="large"
                    suffix={<InputUnitSuffix unit={t('seedTracker:seedInventoryFields.units.percentage')} />}
                  />
                </Form.Item>
                <Form.Item name="diseased_actual" label={t(fk('diseasedAct'))} style={seedInventoryAddFieldCellStyle}>
                  <Input
                    size="large"
                    suffix={<InputUnitSuffix unit={t('seedTracker:seedInventoryFields.units.percentage')} />}
                  />
                </Form.Item>
                <Form.Item name="diseased_estimate" label={t(fk('diseasedEst'))} style={seedInventoryAddFieldCellStyle}>
                  <Input
                    size="large"
                    suffix={<InputUnitSuffix unit={t('seedTracker:seedInventoryFields.units.percentage')} />}
                  />
                </Form.Item>
              </Flex>
            </div>

            <div style={seedInventoryAddSectionBlockStyle}>
              <Typography.Text type="secondary" style={seedInventoryAddSectionTitleStyle}>
                {t(sk('seedingIntentions'))}
              </Typography.Text>
              <Flex gap={16} wrap align="flex-end" style={seedInventoryAddSeedingIntentionsTopRowStyle}>
                <Form.Item label={t(fk('seededPedigree'))} style={seedInventoryAddFieldCellStyle}>
                  {seededPedigreeTag}
                </Form.Item>
                <Form.Item
                  name="projected_pedigree"
                  label={t(fk('projectedPedigree'))}
                  style={seedInventoryAddFieldCellStyle}
                  rules={[{ required: true, message: requiredMsg }]}
                >
                  <Select allowClear options={pedigreeOptions} placeholder={selectPlaceholder} size="large" />
                </Form.Item>
                <Form.Item
                  name="current_seeded_area_actual"
                  label={t(fk('currentSeededAreaAct'))}
                  style={seedInventoryAddFieldCellStyle}
                >
                  <Input
                    size="large"
                    suffix={<InputUnitSuffix unit={t('seedTracker:seedInventoryFields.units.acres')} />}
                  />
                </Form.Item>
                <Form.Item
                  name="current_seeded_area_estimate"
                  label={t(fk('currentSeededAreaEst'))}
                  style={seedInventoryAddFieldCellStyle}
                >
                  <Input
                    size="large"
                    suffix={<InputUnitSuffix unit={t('seedTracker:seedInventoryFields.units.acres')} />}
                  />
                </Form.Item>
                <Form.Item
                  name="projected_yield_actual_bu"
                  label={t(fk('projectedYieldAct'))}
                  style={seedInventoryAddFieldCellStyle}
                >
                  <Input
                    size="large"
                    suffix={<InputUnitSuffix unit={t('seedTracker:seedInventoryFields.units.bu')} />}
                  />
                </Form.Item>
                <Form.Item
                  name="projected_yield_estimate_bu"
                  label={t(fk('projectedYieldEst'))}
                  style={seedInventoryAddFieldCellStyle}
                >
                  <Input
                    size="large"
                    suffix={<InputUnitSuffix unit={t('seedTracker:seedInventoryFields.units.bu')} />}
                  />
                </Form.Item>
              </Flex>
              <Flex gap={16} wrap align="flex-end">
                <Form.Item
                  name="planned_seeded_area"
                  label={t(fk('plannedSeedArea'))}
                  style={seedInventoryAddFieldCellStyle}
                >
                  <Input
                    size="large"
                    suffix={<InputUnitSuffix unit={t('seedTracker:seedInventoryFields.units.acres')} />}
                  />
                </Form.Item>
              </Flex>
            </div>

            <div style={seedInventoryAddSectionBlockStyle}>
              <Typography.Text type="secondary" style={seedInventoryAddSectionTitleStyle}>
                Notes
              </Typography.Text>
              <Form.Item name="note_text" style={{ marginBottom: 0 }}>
                <Input.TextArea placeholder="Add Note..." autoSize={{ minRows: 3, maxRows: 6 }} />
              </Form.Item>
            </div>

            <Form.Item>
              <Space>
                <Button type="primary" htmlType="submit" size="large" loading={submitting} disabled={!productCardId}>
                  {t('save', { ns: 'common' })}
                </Button>
                <Button onClick={goBack} size="large">
                  {t('cancel', { ns: 'common' })}
                </Button>
              </Space>
            </Form.Item>
          </Form>
        </div>
      </Layout.Content>
    </>
  )
}

export default SeedInventoryAdd
